import { useEffect, useMemo, useRef } from 'react';

const MAX_TIMEOUT_MS = 2_147_483_647;
const REFRESH_BUFFER_MS = 1_000;

function getNextScheduledRefreshAt(items) {
  return items.reduce((nextRefreshAt, item) => {
    const scheduledISO = item?.publishedAt ?? item?.post_container?.publishedAt;
    const scheduledAt = scheduledISO ? Date.parse(scheduledISO) : NaN;

    if (!Number.isFinite(scheduledAt)) {
      return nextRefreshAt;
    }

    const refreshAt = scheduledAt + REFRESH_BUFFER_MS;
    return nextRefreshAt === null || refreshAt < nextRefreshAt
      ? refreshAt
      : nextRefreshAt;
  }, null);
}

export default function useScheduledBoardRefresh(queuedItems, refetch) {
  const refetchRef = useRef(refetch);
  const nextRefreshAt = useMemo(
    () => getNextScheduledRefreshAt(queuedItems),
    [queuedItems]
  );

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (nextRefreshAt === null) {
      return undefined;
    }

    let timeoutId;
    let disposed = false;
    let refreshRequested = false;

    const refresh = () => {
      if (disposed || refreshRequested) {
        return;
      }

      refreshRequested = true;
      Promise.resolve(refetchRef.current?.()).catch(() => {
        // The normal cache request path owns error handling and retry policy.
      });
    };

    const scheduleRefresh = () => {
      const remainingMs = nextRefreshAt - Date.now();

      if (remainingMs <= 0) {
        refresh();
        return;
      }

      timeoutId = window.setTimeout(
        scheduleRefresh,
        Math.min(remainingMs, MAX_TIMEOUT_MS)
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Date.now() >= nextRefreshAt) {
        refresh();
      }
    };

    scheduleRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [nextRefreshAt]);
}
